<template>
	<div :style="wrapperStyle" :class="{
		'animated--fade': animation === 'fade'
	}">
		<div
			class="loader-stripes"
			:class="{
				'animated': animation === 'slide',
				'animated--slide': animation === 'slide',
			}"
			:style="progressStyle"
		/>
	</div>
</template>

<script setup>
	import { defineProps, computed } from 'vue';
	import { useElementStyles } from '@/composables/useElementStyles';

	const { getColorOrVariable } = useElementStyles();

	const props = defineProps({
		settings: {
			type: Object,
			required: true
		},
		progress: {
			type: Number,
			default: 0
		},
		animation: {
			type: String,
			default: null
		}
	});

	const background = computed(() => {
		const result = props.settings.background || [];
		return result.map(getColorOrVariable);
	});

	const wrapperStyle = computed(() => {
		const { spacing, size } = props.settings
		const [mt, mr, mb, ml] = spacing?.margin || [0, 0, 0, 0]
		const [pt, pr, pb, pl] = spacing?.padding || [0, 0, 0, 0]
		return {
			width: size?.width || '100%',
			height: `${size?.height}px` || '50px',
			minWidth: size?.minWidth || undefined,
			minHeight: size?.minHeight || undefined,
			maxWidth: size?.maxWidth || undefined,
			maxHeight: size?.maxHeight || undefined,
			margin: `${mt}px ${mr}px ${mb}px ${ml}px`,
			padding: `${pt}px ${pr}px ${pb}px ${pl}px`,
			overflow: 'hidden',
			position: 'relative',
			backgroundColor: background.value[background.value.length - 1] || '#ececec'
		}
	});

	const progressStyle = computed(() => {
		const stripe1 = background.value[0] || '#000000';
		const stripe2 = background.value[1] || stripe1;

		const baseStyle = {
			height: '100%',
			backgroundImage: `repeating-linear-gradient(
      45deg,
      ${stripe1},
      ${stripe1} 55px,
      ${stripe2} 55px,
      ${stripe2} 110px
    )`,
			backgroundSize: '160px 100%'
		};

		if (props.animation) {
			return {
				...baseStyle,
				width: `${props.progress}%`,
			};
		} else {
			return {
				...baseStyle,
				width: `${props.progress}%`,
				animation: 'slide 1s linear infinite'
			};
		}
	});
</script>

<style scoped>
	.animated {
		position: absolute;
		height: 100%;
	}

	.animated--slide {
		animation: slide-infinity 2s ease-in-out infinite;
	}

	.animated--fade {
		animation: fade-infinity 2s ease-in-out infinite;
	}

	@keyframes slide {
		0% {
			background-position: 0 0;
		}
		100% {
			background-position: 40px 0;
		}
	}

	@keyframes slide-infinity {
		0% {
			left: -50%;
		}
		80% {
			left: 100%;
		}
		100% {
			left: 100%;
		}
	}

	@keyframes fade-infinity {
		0%, 100% {
			opacity: 0.5;
		}
		50% {
			opacity: 1;
		}
	}
</style>
